import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Radar } from 'react-chartjs-2';
import { StaffPerformance, StaffMonthlyTrendStats } from '../../types/SupportTypes';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Support Team Detail Component Props
export interface SupportTeamDetailProps {
  staffPerformance: StaffPerformance[];
  monthlyTrendStats: StaffMonthlyTrendStats;
  snapshotStartDate: Date;
  snapshotEndDate: Date;
}

const SupportTeamDetail: React.FC<SupportTeamDetailProps> = ({
  staffPerformance,
  monthlyTrendStats,
  snapshotStartDate,
  snapshotEndDate
}) => {
  // Format time in minutes to a readable format
  const formatResolutionTime = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes.toFixed(0)}m`;
    } else if (minutes < 1440) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = Math.floor(minutes % 60);
      return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
    } else {
      const days = Math.floor(minutes / 1440);
      const remainingMinutes = minutes % 1440;
      const hours = Math.floor(remainingMinutes / 60);
      if (hours > 0) {
        return `${days}d ${hours}h`;
      } else {
        return `${days}d`;
      }
    }
  };

  // Generate a complete list of months between snapshotStartDate and snapshotEndDate
  const standardizedMonths = useMemo(() => {
    const months = [];
    const startDate = new Date(snapshotStartDate);
    const endDate = new Date(snapshotEndDate);

    // Set to first day of month to ensure proper comparison
    const currentDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const lastDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1);

    while (currentDate <= lastDate) {
      const year = currentDate.getFullYear();
      // Add leading zero for months 1-9
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      months.push(`${year}-${month}`);

      // Move to next month
      currentDate.setMonth(currentDate.getMonth() + 1);
    }

    return months;
  }, [snapshotStartDate, snapshotEndDate]);

  // Calculate team aggregate metrics
  const teamAggregateMetrics = useMemo(() => {
    const totalAssignedCases = staffPerformance.reduce((sum, member) => sum + (member.assignedCases || 0), 0);
    const totalStillOpenAssignedCases = staffPerformance.reduce((sum, member) => sum + (member.stillOpenAssignedCases || 0), 0);
    const totalResolvedCases = staffPerformance.reduce((sum, member) => sum + (member.resolvedCases || 0), 0);

    // Calculate weighted average resolution time (weighted by number of resolved cases)
    let weightedResolutionTimeSum = 0;
    let totalCasesWithResolutionTime = 0;

    staffPerformance.forEach(member => {
      if (member.resolvedCases && member.avgResolutionTime) {
        weightedResolutionTimeSum += member.avgResolutionTime * member.resolvedCases;
        totalCasesWithResolutionTime += member.resolvedCases;
      }
    });

    const avgResolutionTime = totalCasesWithResolutionTime > 0
      ? weightedResolutionTimeSum / totalCasesWithResolutionTime
      : 0;

    // Calculate weighted average first response time
    let weightedFirstResponseTimeSum = 0;
    let totalCasesWithFirstResponseTime = 0;

    staffPerformance.forEach(member => {
      if (member.resolvedCases && member.avgFirstResponseTime) {
        weightedFirstResponseTimeSum += member.avgFirstResponseTime * member.resolvedCases;
        totalCasesWithFirstResponseTime += member.resolvedCases;
      }
    });

    const avgFirstResponseTime = totalCasesWithFirstResponseTime > 0
      ? weightedFirstResponseTimeSum / totalCasesWithFirstResponseTime
      : 0;

    // Calculate resolution rate
    const resolutionRate = totalAssignedCases > 0
      ? (totalResolvedCases / totalAssignedCases * 100).toFixed(1)
      : '0.0';

    return {
      totalAssignedCases,
      totalStillOpenAssignedCases,
      totalResolvedCases,
      avgResolutionTime,
      avgFirstResponseTime,
      resolutionRate
    };
  }, [staffPerformance]);

  // Calculate monthly aggregate data for the entire team
  const teamMonthlyData = useMemo(() => {
    const monthlyData: Record<string, {
      assignedCases: number;
      resolvedCases: number;
      avgResolutionTime: number;
      avgFirstResponseTime: number;
      totalResolutionTimeMinutes: number;
      totalFirstResponseTimeMinutes: number;
      casesWithResolutionTime: number;
      casesWithFirstResponseTime: number;
    }> = {};

    // Initialize all months with zero values
    standardizedMonths.forEach(month => {
      monthlyData[month] = {
        assignedCases: 0,
        resolvedCases: 0,
        avgResolutionTime: 0,
        avgFirstResponseTime: 0,
        totalResolutionTimeMinutes: 0,
        totalFirstResponseTimeMinutes: 0,
        casesWithResolutionTime: 0,
        casesWithFirstResponseTime: 0
      };
    });

    // Aggregate data from all team members
    Object.keys(monthlyTrendStats).forEach(memberName => {
      const memberData = monthlyTrendStats[memberName];

      standardizedMonths.forEach(month => {
        const monthData = memberData[month];
        if (monthData) {
          monthlyData[month].assignedCases += monthData.assignedCases || 0;
          monthlyData[month].resolvedCases += monthData.resolvedCases || 0;

          // For averages, we need to track weighted sums and counts
          if (monthData.resolvedCases && monthData.avgResolutionTime) {
            monthlyData[month].totalResolutionTimeMinutes += monthData.avgResolutionTime * monthData.resolvedCases;
            monthlyData[month].casesWithResolutionTime += monthData.resolvedCases;
          }

          if (monthData.resolvedCases && monthData.avgFirstResponseTime) {
            monthlyData[month].totalFirstResponseTimeMinutes += monthData.avgFirstResponseTime * monthData.resolvedCases;
            monthlyData[month].casesWithFirstResponseTime += monthData.resolvedCases;
          }
        }
      });
    });

    // Calculate final averages
    standardizedMonths.forEach(month => {
      if (monthlyData[month].casesWithResolutionTime > 0) {
        monthlyData[month].avgResolutionTime =
          monthlyData[month].totalResolutionTimeMinutes / monthlyData[month].casesWithResolutionTime;
      }

      if (monthlyData[month].casesWithFirstResponseTime > 0) {
        monthlyData[month].avgFirstResponseTime =
          monthlyData[month].totalFirstResponseTimeMinutes / monthlyData[month].casesWithFirstResponseTime;
      }
    });

    return {
      months: standardizedMonths,
      data: standardizedMonths.map(month => monthlyData[month])
    };
  }, [monthlyTrendStats, standardizedMonths]);

  // Prepare data for radar chart - showing assigned and resolved cases by team member
  const casesRadarChartData = useMemo(() => {
    // Sort staff by total cases for better visualization
    const sortedStaff = [...staffPerformance]
      .filter(member => (member.assignedCases || 0) > 0 && (member.resolvedCases || 0) > 0)
      .sort((a, b) => b.name.localeCompare(a.name))
      .slice(0, 150); // Allow for many team members

    return {
      labels: sortedStaff.map(member => member.name),
      datasets: [
        {
          label: 'Cases Assigned',
          data: sortedStaff.map(member => member.assignedCases || 0),
          backgroundColor: 'rgba(59, 130, 246, 0.2)',
          borderColor: 'rgba(59, 130, 246, 1)',
          pointBackgroundColor: 'rgba(59, 130, 246, 1)',
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgba(59, 130, 246, 1)'
        },
        {
          label: 'Cases Resolved',
          data: sortedStaff.map(member => member.resolvedCases || 0),
          backgroundColor: 'rgba(16, 185, 129, 0.2)',
          borderColor: 'rgba(16, 185, 129, 1)',
          pointBackgroundColor: 'rgba(16, 185, 129, 1)',
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgba(16, 185, 129, 1)'
        }
      ]
    };
  }, [staffPerformance]);

  // Prepare data for radar chart - showing average response and resolution times by team member
  const timeRadarChartData = useMemo(() => {
    // Filter staff with valid resolution and response times
    const staffWithTimes = [...staffPerformance]
      .filter(member =>
        member.avgResolutionTime > 0 &&
        member.avgFirstResponseTime > 0 &&
        (member.assignedCases || 0) > 0 &&
        (member.resolvedCases || 0) > 0
      )
      // Sort by resolution time for better visualization
      .sort((a, b) => b.name.localeCompare(a.name))
      .slice(0, 150); // Allow for many team members

    return {
      labels: staffWithTimes.map(member => member.name),
      datasets: [
        {
          label: 'Avg Resolution Time (hours)',
          data: staffWithTimes.map(member => member.avgResolutionTime / 60), // Convert minutes to hours
          backgroundColor: 'rgba(245, 158, 11, 0.2)',
          borderColor: 'rgba(245, 158, 11, 1)',
          pointBackgroundColor: 'rgba(245, 158, 11, 1)',
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgba(245, 158, 11, 1)'
        },
        {
          label: 'Avg First Response Time (hours)',
          data: staffWithTimes.map(member => member.avgFirstResponseTime / 60), // Convert minutes to hours
          backgroundColor: 'rgba(239, 68, 68, 0.2)',
          borderColor: 'rgba(239, 68, 68, 1)',
          pointBackgroundColor: 'rgba(239, 68, 68, 1)',
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgba(239, 68, 68, 1)'
        }
      ]
    };
  }, [staffPerformance]);

  // Chart data for team cases trend
  const casesChartData = {
    labels: teamMonthlyData.months,
    datasets: [
      {
        label: 'Cases Assigned',
        data: teamMonthlyData.data.map(d => d.assignedCases),
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true
      },
      {
        label: 'Cases Resolved',
        data: teamMonthlyData.data.map(d => d.resolvedCases),
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  };

  // Chart data for team resolution time trend
  const resolutionTimeChartData = {
    labels: teamMonthlyData.months,
    datasets: [
      {
        label: 'Avg Resolution Time (hours)',
        data: teamMonthlyData.data.map(d => d.avgResolutionTime / 60),
        borderColor: '#f59e0b',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        tension: 0.4,
        fill: true
      },
      {
        label: 'First Response Time (hours)',
        data: teamMonthlyData.data.map(d => d.avgFirstResponseTime / 60),
        borderColor: '#ef4444',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const radarChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      r: {
        beginAtZero: true,
      },
    },
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
      height: '100%'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{
          margin: '0 0 8px 0',
          fontSize: '24px',
          fontWeight: 'bold',
          color: '#1f2937'
        }}>
          Support Team Overview
        </h2>
        <p style={{
          margin: 0,
          color: '#6b7280',
          fontSize: '16px'
        }}>
          Aggregate performance across all team members
        </p>
      </div>

      {/* Key Metrics Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '32px'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.9 }}>Assigned Cases - Open/Total</h3>
          <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>
          {teamAggregateMetrics.totalStillOpenAssignedCases} / {teamAggregateMetrics.totalAssignedCases}
          </p>
        </div>

        <div style={{
          background: 'linear-gradient(135deg,rgb(62, 217, 245) 0%, #3b82f6 100%)',
          color: 'white',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.9 }}>Total Cases Resolved</h3>
          <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>
            {teamAggregateMetrics.totalResolvedCases}
          </p>
        </div>

        <div style={{
          background: 'linear-gradient(135deg,rgb(82, 247, 192) 0%, #059669 100%)',
          color: 'white',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.9 }}>Team Resolution Rate</h3>
          <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>
            {teamAggregateMetrics.resolutionRate}%
          </p>
        </div>

        <div style={{
          background: 'linear-gradient(135deg,rgb(239, 192, 110) 0%, #d97706 100%)',
          color: 'white',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.9 }}>Avg Resolution Time</h3>
          <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>
            {formatResolutionTime(teamAggregateMetrics.avgResolutionTime)}
          </p>
        </div>

        <div style={{
          background: 'linear-gradient(135deg,rgb(239, 143, 226) 0%,rgb(229, 61, 241) 100%)',
          color: 'white',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.9 }}>Avg First Response Time</h3>
          <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>
            {formatResolutionTime(teamAggregateMetrics.avgFirstResponseTime)}
          </p>
        </div>
      </div>

      {/* Two-column flex grid for charts */}

      {/* Row 1: Cases Charts */}
      <div style={{ marginBottom: '32px' }}>
        {/* <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#4b5563', textAlign: 'center' }}>
          Case Distribution and Trends
        </h3> */}
        <div style={{ display: 'flex', flexDirection: 'row', gap: '24px', flexWrap: 'wrap' }}>
          {/* Cases Radar Chart */}
          <div style={{ flex: '1 1 450px', minWidth: '300px' }}>
            <h4 style={{ margin: '0 0 6px 0', fontSize: '16px', color: '#4b5563' }}>
              Case Distribution by Team Member
            </h4>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', opacity: 0.9 }}>
              Filtered to show members with at least 1 assigned and resolved case.
            </p>
            <div style={{ height: '350px' }}>
              <Radar data={casesRadarChartData} options={radarChartOptions} />
            </div>
          </div>

          {/* Cases Line Chart */}
          <div style={{ flex: '1 1 450px', minWidth: '300px' }}>
            <h4 style={{ margin: '0 0 6px 0', fontSize: '16px', color: '#4b5563' }}>
              Team Case Handling Efficiency
            </h4>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', opacity: 0.9 }}>
              Cases assigned vs. resolved across the snapshot period.
            </p>
            <div style={{ height: '350px' }}>
              <Line data={casesChartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Response/Resolution Time Charts */}
      <div>
        {/* <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#4b5563', textAlign: 'center' }}>
          Response and Resolution Time Analysis
        </h3> */}
        <div style={{ display: 'flex', flexDirection: 'row', gap: '24px', flexWrap: 'wrap' }}>
          {/* Response/Resolution Time Radar Chart */}
          <div style={{ flex: '1 1 450px', minWidth: '300px' }}>
            <h4 style={{ margin: '0 0 6px 0', fontSize: '16px', color: '#4b5563' }}>
              Average Response & Resolution Time by Team Member
            </h4>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', opacity: 0.9 }}>
              Filtered to show members with valid response and resolution times (in hours).
            </p>
            <div style={{ height: '350px' }}>
              <Radar data={timeRadarChartData} options={radarChartOptions} />
            </div>
          </div>

          {/* Response/Resolution Time Line Chart */}
          <div style={{ flex: '1 1 450px', minWidth: '300px' }}>
            <h4 style={{ margin: '0 0 6px 0', fontSize: '16px', color: '#4b5563' }}>
              Team Response & Resolution Time Trend
            </h4>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', opacity: 0.9 }}>
              Average times across all team members over the snapshot period.
            </p>
            <div style={{ height: '350px' }}>
              <Line data={resolutionTimeChartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportTeamDetail;
